import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { KeyRound, Loader2, ShieldCheck, Settings, ChevronRight, X } from "lucide-react";


function loadMidtransSnap(clientKey, snapUrl) {
  const source = snapUrl || "https://app.sandbox.midtrans.com/snap/snap.js";
  let parsed;
  try { parsed = new URL(source); } catch { throw new Error("URL Midtrans tidak valid."); }
  const allowedOrigins = new Set([
    "https://app.sandbox.midtrans.com",
    "https://app.midtrans.com",
  ]);
  if (!allowedOrigins.has(parsed.origin) || parsed.pathname !== "/snap/snap.js") {
    throw new Error("Sumber pembayaran Midtrans tidak diizinkan.");
  }

  const existing = document.querySelector('script[data-pms-midtrans="true"]');
  if (window.snap) {
    if (!existing || (existing.src === parsed.href && existing.dataset.clientKey === clientKey)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Lingkungan pembayaran berubah. Muat ulang halaman."));
  }
  if (existing) {
    if (existing.src !== parsed.href || existing.dataset.clientKey !== clientKey) {
      return Promise.reject(new Error("Lingkungan pembayaran tidak cocok. Muat ulang halaman."));
    }
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => window.snap ? resolve() : reject(new Error("Midtrans Snap gagal dimuat.")), { once: true });
      existing.addEventListener("error", () => reject(new Error("Tidak dapat memuat pembayaran Midtrans.")), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = parsed.href;
    script.dataset.clientKey = clientKey;
    script.dataset.pmsMidtrans = "true";
    script.async = true;
    script.onload = () => window.snap ? resolve() : reject(new Error("Midtrans Snap gagal dimuat."));
    script.onerror = () => reject(new Error("Tidak dapat memuat pembayaran Midtrans."));
    document.head.appendChild(script);
  });
}

export default function Login() {
  const { loginWithKey, logoutToAccount } = useAuth();
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [orderNotificationOpen, setOrderNotificationOpen] = useState(false);

  useEffect(() => {
    if (!orderNotificationOpen) return undefined;
    const timer = setTimeout(() => setOrderNotificationOpen(false), 3000);
    return () => clearTimeout(timer);
  }, [orderNotificationOpen]);

  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const [apiContactOpen, setApiContactOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [expiredKeyForRenewal, setExpiredKeyForRenewal] = useState("");
  const [expiredLockPage, setExpiredLockPage] = useState(() => localStorage.getItem("pp_expired") === "1");
  const [renewalStep, setRenewalStep] = useState("locked");
  const [renewalOtp, setRenewalOtp] = useState("");
  const [renewalEmailHint, setRenewalEmailHint] = useState("");
  const [renewalEmailInput, setRenewalEmailInput] = useState("");
  const [renewalEmailRequired, setRenewalEmailRequired] = useState(false);
  const [renewalToken, setRenewalToken] = useState("");
  const [paidPricing, setPaidPricing] = useState(null);
  const [renewalBusy, setRenewalBusy] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const [renewalFromReminder, setRenewalFromReminder] = useState(false);
  const [storeSubmitting, setStoreSubmitting] = useState(false);
  const [checkoutProcessingOpen, setCheckoutProcessingOpen] = useState(false);
  const [midtransOpen, setMidtransOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState(() => {
    const savedOrderId = localStorage.getItem("pinmaker-last-order-id") || "";
    return savedOrderId ? {
      status: "checking",
      order_id: savedOrderId,
      plan_name: "",
      order_type: "purchase",
    } : null;
  });
  const [paymentOrderId, setPaymentOrderId] = useState(
  () => localStorage.getItem("pinmaker-last-order-id") || ""
);
  const [paymentResumeToken, setPaymentResumeToken] = useState(() => {
    const id = localStorage.getItem("pinmaker-last-order-id") || "";
    return id ? localStorage.getItem("pinmaker-resume-token-" + id) || "" : "";
  });
  const [dismissedPaymentOrderIds, setDismissedPaymentOrderIds] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("pinmaker-dismissed-payment-notifications") || "[]"
      );
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const [resumingPayment, setResumingPayment] = useState(false);
  const [cancellingPayment, setCancellingPayment] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      const shared = localStorage.getItem("pms-theme");
      if (shared === "dark" || shared === "light") return shared;

      const legacy = localStorage.getItem("pinmaker-login-theme");
      if (legacy === "dark" || legacy === "light") return legacy;

      return "dark";
    } catch {
      return "dark";
    }
  });
  const [language] = useState(() =>
    localStorage.getItem("pinmaker-login-language") || "id"
  );

  const en = language === "en";
  const dark = theme === "dark";

  const t = en ? {
    title: "PIN Maker",
    subtitle: "Enter your API Key to continue",
    placeholder: "API Key",
    login: "Sign In",
    loading: "Signing in...",
    settings: "Settings",
    admin: "Admin Page",
    adminDesc: "Sign in as administrator",
    theme: "Theme",
    light: "Light",
    dark: "Dark",
    language: "Language",
    indonesia: "Indonesia",
    english: "English",
    empty: "Please enter your API Key",
    success: "Signed in successfully",
    invalid: "Invalid API Key",
    expired: "Your API Key has expired. Contact the admin to extend it.",
    session: "You were signed out because you logged in on another device",
  } : {
    title: "PIN Maker",
    subtitle: "Masukkan API Key untuk melanjutkan",
    placeholder: "API Key",
    login: "Masuk",
    loading: "Sedang masuk...",
    settings: "Setelan",
    admin: "Halaman Admin",
    adminDesc: "Login sebagai admin",
    theme: "Tema",
    light: "Light",
    dark: "Dark",
    language: "Bahasa",
    indonesia: "Indonesia",
    english: "English",
    empty: "Masukkan API Key",
    success: "Berhasil masuk",
    invalid: "API Key tidak valid",
    expired: "Masa aktif API key sudah habis. Hubungi admin untuk memperpanjang.",
    session: "Anda keluar karena login di perangkat lain",
  };

  useEffect(() => {
    try {
      localStorage.setItem("pms-theme", theme);
      localStorage.setItem("pinmaker-login-theme", theme);
      localStorage.setItem("pinmaker-login-language", language);
    } catch {}
  }, [theme, language]);

  // PMS_SHARED_THEME_SYNC_LOGIN_V1
  useEffect(() => {
    const syncTheme = (event) => {
      const next = event?.detail?.theme;
      if (next === "dark" || next === "light") {
        setTheme(current => current === next ? current : next);
      }
    };
    window.addEventListener("pms-theme-change", syncTheme);
    return () => window.removeEventListener("pms-theme-change", syncTheme);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("pms-theme-change", { detail: { theme } })
    );
  }, [theme]);

  useEffect(() => {
    let active = true;
    api.get("/store/renewal/pricing")
      .then((response) => {
        if (active) setPaidPricing(response.data?.packages || null);
      })
      .catch(() => {
        // Checkout tetap divalidasi server; default hanya dipakai sebagai fallback tampilan.
      });
    return () => { active = false; };
  }, []);

  const getPlanPricing = (planId, fallbackPrice, fallbackDays) => {
    const plan = paidPricing?.[planId] || {};
    const originalPrice = Number(plan.original_price ?? fallbackPrice);
    const discountPrice = plan.discount_price == null ? null : Number(plan.discount_price);
    const discountActive = Boolean(
      plan.discount_active && Number.isFinite(discountPrice) &&
      discountPrice > 0 && discountPrice < originalPrice
    );
    return {
      originalPrice,
      discountPrice,
      discountActive,
      price: discountActive ? discountPrice : Number(plan.price ?? originalPrice),
      days: Number(plan.days ?? fallbackDays),
    };
  };

  const formatRupiah = (value) =>
    `Rp${Math.max(0, Number(value) || 0).toLocaleString("id-ID")}`;

  useEffect(() => {
    if (localStorage.getItem("pp_expired") === "1") {
      // Keep the lock marker until the user explicitly exits or renewal succeeds.
      if (localStorage.getItem("pp_expired_notice_shown") !== "1") {
        localStorage.setItem("pp_expired_notice_shown", "1");
        setTimeout(() => toast.error(t.expired, { duration: 5000 }), 700);
      }
    } else if (localStorage.getItem("pp_session_ended") === "1") {
      localStorage.setItem("pp_session_ended", "shown");
      setTimeout(() => {
        toast.warning(t.session, { duration: 4000 });
        localStorage.removeItem("pp_session_ended");
      }, 700);
    }
  }, [t.expired, t.session]);

  const beginRenewalVerification = async (apiKey, suppliedEmail = "") => {
    const cleanKey = String(apiKey || "").trim();
    const keyChanged = cleanKey !== expiredKeyForRenewal;
    const cleanEmail = keyChanged ? "" : String(suppliedEmail || "").trim().toLowerCase();
    if (keyChanged) setRenewalEmailInput("");
    if (!cleanKey) {
      toast.error(en ? "Enter your API Key first." : "Masukkan API Key terlebih dahulu.");
      return;
    }
    setExpiredKeyForRenewal(cleanKey);
    setExpiredLockPage(true);
    setRenewalBusy(true);
    try {
      const response = await api.post("/store/renewal/request-otp", {
        key: cleanKey,
        ...(cleanEmail ? { email: cleanEmail } : {}),
      });
      if (response.data?.email_required) {
        setRenewalEmailRequired(true);
        setRenewalEmailInput(cleanEmail);
        setRenewalEmailHint("");
        setRenewalStep("locked");
        toast.info(en
          ? "Enter an email address to verify ownership of this API Key."
          : "Masukkan email untuk memverifikasi kepemilikan API Key ini.");
        return;
      }
      setRenewalEmailRequired(false);
      setRenewalEmailHint(response.data?.email_hint || "email terdaftar");
      setRenewalStep("otp");
      setRenewalOtp("");
      setRenewalToken("");
      toast.success(en ? "Verification code sent to the owner email." : "Kode verifikasi dikirim ke email pemilik.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || (en ? "Could not send the verification code." : "Kode verifikasi belum dapat dikirim."));
    } finally {
      setRenewalBusy(false);
    }
  };

  const verifyRenewalOtp = async () => {
    if (!expiredKeyForRenewal || !/^\d{6}$/.test(renewalOtp.trim())) {
      toast.error(en ? "Enter the 6-digit verification code." : "Masukkan kode verifikasi 6 angka.");
      return;
    }
    setRenewalBusy(true);
    try {
      const response = await api.post("/store/renewal/verify-otp", {
        key: expiredKeyForRenewal,
        otp: renewalOtp.trim(),
      });
      setRenewalToken(response.data?.renewal_token || "");
      setRenewalEmailRequired(false);
      setRenewalStep("plans");
      toast.success(en ? "API Key ownership verified." : "Kepemilikan API Key berhasil diverifikasi.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (en ? "Verification failed." : "Verifikasi gagal."));
    } finally {
      setRenewalBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) {
      toast.error(t.empty);
      return;
    }

    setLoading(true);
    try {
      if (localStorage.getItem("pms-renewal-start") === "1") {
        localStorage.removeItem("pms-renewal-start");
        setRenewalFromReminder(true);
        await beginRenewalVerification(key.trim());
        return;
      }
      await loginWithKey(key.trim());
      toast.success(t.success);
      navigate("/");
    } catch (err) {
      const detail = String(err?.response?.data?.detail || "").toLowerCase();
      if (err?.response?.status === 403 && (detail.includes("habis") || detail.includes("masa aktif") || detail.includes("expired"))) {
        localStorage.setItem("pp_expired", "1");
        setExpiredKeyForRenewal(key.trim());
        setExpiredLockPage(true);
        setRenewalFromReminder(false);
        setRenewalStep("locked");
        setRenewalOtp("");
        setRenewalToken("");
      } else {
        toast.error(err?.response?.data?.detail || t.invalid);
      }
    } finally {
      setLoading(false);
    }
  };


  const runStoreCheckout = async () => {
    setCheckoutProcessingOpen(true);
    setConfirmOpen(false);
    setStoreSubmitting(true);
                          try {
                            if (selectedPlan === "demo") {
                              await api.post("/store/demo", {
                                name: buyerName.trim(),
                                email: buyerEmail.trim(),
                                phone: buyerPhone.trim(),
                              });

                              toast.success(
                                en
                                  ? "Demo API Key sent. Please check your email."
                                  : "API Key demo dikirim. Silakan periksa email kamu."
                              );
                              setApiContactOpen(false);
                              setSelectedPlan(null);
                              setBuyerName("");
                              setBuyerEmail("");
                              setBuyerPhone("");
                              return;
                            }

                            const response = await api.post("/store/checkout", {
                              plan: selectedPlan,
                              name: buyerName.trim(),
                              email: buyerEmail.trim(),
                              phone: buyerPhone.trim(),
                            });
                            const checkout = response.data;
                            if (checkout.order_id) {
                              localStorage.setItem("pinmaker-last-order-id", checkout.order_id);
                              if (checkout.resume_token) {
                                localStorage.setItem(
                                  "pinmaker-resume-token-" + checkout.order_id,
                                  checkout.resume_token
                                );
                                setPaymentResumeToken(checkout.resume_token);
                              }
                              setPaymentOrderId(checkout.order_id);
                              setPaymentNotice({
                                status: "checking",
                                order_id: checkout.order_id,
                                plan_name: checkout.plan || "",
                              });
                            }

                            if (!checkout.snap_token || !checkout.client_key) {
  throw new Error("Data pembayaran dari server tidak lengkap.");
}

await loadMidtransSnap(checkout.client_key, checkout.snap_url);

// Tutup modal pilihan paket agar tidak menutupi Snap.
setApiContactOpen(false);
setCheckoutProcessingOpen(true);
setMidtransOpen(true);
window.snap.pay(checkout.snap_token, {
  onSuccess: () => {
    setMidtransOpen(false);
    setPaymentNotice({
      status: "checking",
      order_id: checkout.order_id,
      plan_name: checkout.plan || "",
    });
                                toast.info(en
                                  ? "Payment pending. Complete the payment instructions."
                                  : "Pembayaran tertunda. Selesaikan instruksi pembayaran.");
                              },
                              onError: () => {
                                setMidtransOpen(false);
                                setPaymentNotice({
                                  status: "failed",
                                  order_id: checkout.order_id,
                                  plan_name: checkout.plan || "",
                                });
                                setRetryOrderId(checkout.order_id || "");
                                toast.error(en ? "Payment failed." : "Pembayaran gagal.");
                              },
                              onClose: () => {
                                setMidtransOpen(false);
                                setPaymentNotice((old) => (
                                  old?.status === "paid"
                                    ? old
                                    : {
                                        ...(old || {}),
                                        status: "checking",
                                        order_id: checkout.order_id,
                                        plan_name: checkout.plan || "",
                                      }
                                ));
                                setCheckoutProcessingOpen(true);
                                toast.info(
                                  en
                                    ? "Payment window closed. Your order is still pending."
                                    : "Jendela pembayaran ditutup. Pesanan masih menunggu pembayaran."
                                );
                              },
                            });
                          } catch (error) {
                            setCheckoutProcessingOpen(false);
                            toast.error(
                              error?.response?.data?.detail ||
                              error?.message ||
                              (en ? "Checkout failed." : "Checkout gagal.")
                            );
                          } finally {
                            setStoreSubmitting(false);
                          }

  };



  useEffect(() => {
    if (!paymentOrderId) return;
    let stopped = false;
    let timer;
    const checkStatus = async () => {
            if (stopped) return;
      if (midtransOpen) {
        timer = setTimeout(checkStatus, 4000);
        return;
      }
try {
        const response = await api.get(
          "/store/order-status/" + encodeURIComponent(paymentOrderId)
        );
        if (stopped) return;
        const status = String(response.data?.status || "").toLowerCase();
        if (status === "paid") {
          setPaymentNotice({
            status: "paid",
            order_id: paymentOrderId,
            plan_name: response.data?.plan_name || "",
            order_type: response.data?.order_type || "purchase",
            renewal_expires_at: response.data?.renewal_expires_at || "",
          });
          setCheckoutProcessingOpen(false);
          setApiContactOpen(true);
          setRetryOrderId("");
          return;
        }
        if (["failed", "cancel", "cancelled", "expire", "expired", "checkout_error"].includes(status)) {
          const finalStatus =
            status === "cancel" || status === "cancelled" ? "cancelled" :
            status === "expire" || status === "expired" ? "expired" : "failed";

          setPaymentNotice({
            status: finalStatus,
            order_id: paymentOrderId,
            plan_name: response.data?.plan_name || "",
            order_type: response.data?.order_type || "purchase",
          });
          setRetryOrderId(paymentOrderId);
          return;
        }

        setPaymentNotice((old) => {
            const base = old?.order_id === paymentOrderId
              ? old
              : {
                  order_id: paymentOrderId,
                  plan_name: response.data?.plan_name || "",
                  order_type: response.data?.order_type || "purchase",
                };

            if (status === "pending") {
              return {
                ...base,
                status: "pending",
                plan_name: response.data?.plan_name || base.plan_name,
                order_type: response.data?.order_type || base.order_type,
              };
            }

            if (["processing", "authorize"].includes(status)) {
              return {
                ...base,
                status: "checking",
                plan_name: response.data?.plan_name || base.plan_name,
                order_type: response.data?.order_type || base.order_type,
              };
            }

            return base;
          });
      } catch (_) {
        // Status endpoint may temporarily be unavailable; retry on the next poll.
      }
      if (!stopped) timer = setTimeout(checkStatus, 4000);
    };
    checkStatus();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [paymentOrderId, midtransOpen]);

  const resumePayment = async () => {
    const canResumeRenewal = paymentNotice?.order_type === "renewal" && !!renewalToken;
    if (!paymentOrderId || (!paymentResumeToken && !canResumeRenewal) || resumingPayment) return;
    setResumingPayment(true);
    setPaymentNotice((old) => old?.status === "paid"
      ? old
      : { ...(old || {}), status: "checking" });
    setCheckoutProcessingOpen(true);
    try {
      const response = canResumeRenewal
        ? await api.post(`/store/renewal/pending/${encodeURIComponent(paymentOrderId)}/resume`, { renewal_token: renewalToken })
        : await api.post("/store/resume-payment", {
            order_id: paymentOrderId,
            resume_token: paymentResumeToken,
          });
      const checkout = response.data;
      await loadMidtransSnap(checkout.client_key, checkout.snap_url);
      setCheckoutProcessingOpen(true);
      setMidtransOpen(true);
      window.snap.pay(checkout.snap_token, {
        onSuccess: () => {
          setMidtransOpen(false);
            setPaymentNotice({
              status: "checking", order_id: paymentOrderId,
              plan_name: paymentNotice?.plan_name || "",
            });
            setCheckoutProcessingOpen(true);
          },
        onPending: () => {
            setPaymentNotice({
              status: "pending", order_id: paymentOrderId,
              plan_name: paymentNotice?.plan_name || "",
            });
            setCheckoutProcessingOpen(true);
          },
        onError: () => {
          setMidtransOpen(false);
          setPaymentNotice({
            status: "failed", order_id: paymentOrderId,
            plan_name: paymentNotice?.plan_name || "",
          });
          toast.error(en ? "Payment failed." : "Pembayaran gagal.");
        },
        onClose: () => {
          setMidtransOpen(false);
          setPaymentNotice((old) => old?.status === "paid"
            ? old
            : { ...(old || {}), status: "checking" });
          setCheckoutProcessingOpen(true);
          toast.info(
            en ? "Payment window closed. Checking order status."
               : "Jendela pembayaran ditutup. Memeriksa status pesanan."
          );
        },
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        (en ? "Unable to resume payment." : "Pembayaran belum bisa dilanjutkan.")
      );
    } finally {
      setResumingPayment(false);
    }
  };

  const cancelCurrentPendingPayment = async () => {
    if (!paymentOrderId || !["pending", "checking"].includes(paymentNotice?.status) || cancellingPayment) return;
    setCancellingPayment(true);
    try {
      let response;
      if (paymentNotice?.order_type === "renewal" && renewalToken) {
        response = await api.post(`/store/renewal/pending/${encodeURIComponent(paymentOrderId)}/cancel`, {
          renewal_token: renewalToken,
        });
      } else if (paymentResumeToken) {
        response = await api.post("/store/cancel-payment", {
          order_id: paymentOrderId,
          resume_token: paymentResumeToken,
        });
      } else {
        throw new Error(en ? "Cancellation access has expired. Verify the API Key again." : "Akses pembatalan berakhir. Verifikasi API Key kembali.");
      }
      setPaymentNotice((old) => ({ ...(old || {}), status: response.data?.status || "cancelled" }));
      setRenewalToken("");
      setCheckoutProcessingOpen(false);
      toast.success(en ? "Order cancelled." : "Pesanan berhasil dibatalkan.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || (en
          ? "Midtrans has not confirmed cancellation. Do not create another payment yet."
          : "Midtrans belum mengonfirmasi pembatalan. Jangan membuat pembayaran baru dulu."));
    } finally {
      setCancellingPayment(false);
    }
  };

  const startRenewalCheckout = async (planId, replacePendingOrderId = "") => {
    if (!renewalToken || renewalBusy) return;
    setRenewalBusy(true);
    try {
      const checkoutPayload = { plan: planId, renewal_token: renewalToken };
      if (replacePendingOrderId) checkoutPayload.replace_pending_order_id = replacePendingOrderId;
      const response = await api.post("/store/renewal/checkout", checkoutPayload);
      const checkout = response.data;
      if (!checkout.snap_token || !checkout.client_key || !checkout.order_id) {
        throw new Error(en ? "Incomplete payment information." : "Data pembayaran tidak lengkap.");
      }
      localStorage.setItem("pinmaker-last-order-id", checkout.order_id);
      if (checkout.resume_token) {
        localStorage.setItem("pinmaker-resume-token-" + checkout.order_id, checkout.resume_token);
        setPaymentResumeToken(checkout.resume_token);
      }
      setPaymentOrderId(checkout.order_id);
      setPaymentNotice({
        status: "checking",
        order_id: checkout.order_id,
        plan_name: checkout.plan || "",
        order_type: "renewal",
      });
      setCheckoutProcessingOpen(true);
      await loadMidtransSnap(checkout.client_key, checkout.snap_url);
      setCheckoutProcessingOpen(true);
      setMidtransOpen(true);
      window.snap.pay(checkout.snap_token, {
        onSuccess: () => {
          setMidtransOpen(false);
          setPaymentNotice({ status: "checking", order_id: checkout.order_id, plan_name: checkout.plan || "", order_type: "renewal" });
          setCheckoutProcessingOpen(true);
        },
        onPending: () => {
          setPaymentNotice({ status: "pending", order_id: checkout.order_id, plan_name: checkout.plan || "", order_type: "renewal" });
          setCheckoutProcessingOpen(true);
        },
        onError: () => {
          setMidtransOpen(false);
          setPaymentNotice({ status: "failed", order_id: checkout.order_id, plan_name: checkout.plan || "", order_type: "renewal" });
          setCheckoutProcessingOpen(false);
          setApiContactOpen(true);
        },
        onClose: () => {
          setMidtransOpen(false);
          setPaymentNotice((old) => old?.status === "paid" ? old : { ...(old || {}), status: "checking", order_id: checkout.order_id, plan_name: checkout.plan || "", order_type: "renewal" });
          setCheckoutProcessingOpen(true);
        },
      });
    } catch (error) {
      setCheckoutProcessingOpen(false);
      const detail = error?.response?.data?.detail;
      const httpStatus = error?.response?.status;
      if (detail && typeof detail === "object" && detail.code === "PENDING_PACKAGE_CHANGE_REQUIRED") {
        setPendingPlanChange(detail);
        return;
      }
      if (httpStatus === 409) {
        const message = typeof detail === "string" ? detail : (detail?.message || (en ? "The existing payment is not yet safe to replace." : "Transaksi lama belum aman untuk diganti."));
        toast.error(message);
        return;
      }
      setRenewalToken("");
      setRenewalStep("locked");
      toast.error(typeof detail === "string" ? detail : (detail?.message || error?.message || (en ? "Renewal checkout failed. Verify your API Key again." : "Checkout perpanjangan gagal. Silakan verifikasi API Key kembali.")));
    } finally {
      setRenewalBusy(false);
    }
  };

  return (
    <div className={`relative flex min-h-screen items-center justify-center overflow-hidden px-4 ${expiredLockPage ? "py-8" : "py-16"} transition-colors duration-300 ${
      dark
        ? "bg-[#07132e] text-slate-100"
        : "bg-gradient-to-br from-[#edf5ff] via-white to-[#d9eaff] text-slate-900"
    }`} style={{ position: "relative" }}>
      <style>{`
        @keyframes pms-blue-drift {
          0%,100% { transform: translate3d(0,0,0) scale(1); opacity:.45; }
          50% { transform: translate3d(42px,30px,0) scale(1.12); opacity:.8; }
        }
        @keyframes pms-violet-drift {
          0%,100% { transform: translate3d(0,0,0) scale(1); opacity:.35; }
          50% { transform: translate3d(-36px,-26px,0) scale(1.1); opacity:.7; }
        }
        @keyframes pms-card-in {
          from { opacity:0; transform:translateY(18px) scale(.985); }
          to { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes pms-twinkle {
          0%,100% { opacity:.2; transform:scale(.7); }
          50% { opacity:.85; transform:scale(1.2); }
        }
        .pms-login-glow-blue { animation:pms-blue-drift 13s ease-in-out infinite; }
        .pms-login-glow-violet { animation:pms-violet-drift 16s ease-in-out infinite; }
        .pms-login-star { animation:pms-twinkle 3s ease-in-out infinite; }
        .pms-login-card { animation:pms-card-in .65s cubic-bezier(.2,.8,.2,1) both; }

        @keyframes pms-menu-panel-in {
          from {
            opacity: 0;
            transform: translateY(-9px) scale(.96);
            filter: blur(2px);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        
        .pms-menu-panel {
          transform-origin: top right;
          animation: pms-menu-panel-in .26s cubic-bezier(.2,.8,.2,1) both;
        }
        @media (prefers-reduced-motion:reduce) {
          .pms-login-glow-blue,.pms-login-glow-violet,.pms-login-star,.pms-login-card,.pms-menu-dots,.pms-menu-panel,
        }
      `}</style>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="pms-login-glow-blue absolute -left-24 -top-24 h-80 w-80 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="pms-login-glow-violet absolute -bottom-28 -right-24 h-96 w-96 rounded-full bg-violet-600/20 blur-3xl" />
        <span className="pms-login-star absolute left-[17%] top-[19%] h-1 w-1 rounded-full bg-blue-200/70" />
        <span className="pms-login-star absolute left-[77%] top-[27%] h-1.5 w-1.5 rounded-full bg-violet-200/70" style={{ animationDelay: "1s" }} />
        <span className="pms-login-star absolute left-[28%] top-[72%] h-1 w-1 rounded-full bg-cyan-200/60" style={{ animationDelay: "1.7s" }} />
        <span className="pms-login-star absolute left-[84%] top-[68%] h-1 w-1 rounded-full bg-blue-200/70" style={{ animationDelay: "0.6s" }} />
      </div>

      <div className={`${expiredLockPage ? "hidden" : "absolute right-5 top-5 z-20 flex items-center gap-2 sm:right-8 sm:top-7"}`}>
       <div className="relative">
  <Button
    type="button"
    variant="outline"
    size="icon"
    aria-label={en ? "Open menu" : "Buka menu"}
    title={en ? "Menu" : "Menu"}
    onClick={() => {
      setOrderNotificationOpen(false);
      setTopMenuOpen((old) => !old);
    }}
    className={`flex h-10 w-10 items-center justify-center rounded-xl border shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
      dark
        ? "border-cyan-300/50 bg-[#12315a] text-cyan-100 shadow-cyan-900/30 hover:bg-[#1b4778]"
        : "border-blue-600 bg-blue-600 text-white shadow-blue-600/30 hover:bg-blue-700"
    }`}
  >
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="12" cy="3.5" r="2.1" />
      <circle cx="12" cy="12" r="2.1" />
      <circle cx="12" cy="20.5" r="2.1" />
    </svg>
  </Button>
  {topMenuOpen && (
    <div className={`pms-menu-panel absolute right-0 top-14 z-50 w-60 rounded-2xl border p-2 shadow-2xl ${
      dark ? "border-blue-300/20 !bg-[#101d3a] text-slate-100" : "border-blue-200 !bg-[#eff6ff] text-slate-900"
    }`}>
      <button
        type="button"
        onClick={() => {
          setOrderNotificationOpen(true);
          setTopMenuOpen(false);
        }}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${
          dark ? "hover:bg-[#1b3d70]" : "hover:bg-[#dbeafe]"
        }`}
      >
        
        <span className="flex-1">{en ? "Notifications / Order status" : "Notifikasi / Status pesanan"}</span>
        {paymentOrderId && !dismissedPaymentOrderIds.includes(paymentOrderId) && (
          <span className="h-2 w-2 rounded-full bg-amber-500" />
        )}
      </button>

      <button
        type="button"
        onClick={() => {
          setOrderNotificationOpen(false);
          setSupportOpen(true);
          setTopMenuOpen(false);
        }}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${
          dark ? "hover:bg-[#1b3d70]" : "hover:bg-[#dbeafe]"
        }`}
      >
        
        <span>{en ? "Contact Support" : "Contact Support"}</span>
      </button>

      <div className={`my-1 border-t ${dark ? "border-slate-700" : "border-slate-200"}`} />

      <button
        type="button"
        onClick={() => {
          setOrderNotificationOpen(false);
          setLogoutConfirmOpen(true);
          setTopMenuOpen(false);
        }}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-red-400 transition-colors ${
          dark ? "hover:bg-red-950/40" : "hover:bg-red-50"
        }`}
      >
        
        <span>{en ? "Log out" : "Keluar"}</span>
      </button>
    </div>
  )}


  {paymentOrderId && !dismissedPaymentOrderIds.includes(paymentOrderId) && (
    <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500" />
  )}

  {orderNotificationOpen && (
    <div className={`pms-menu-panel absolute right-0 top-14 w-80 rounded-2xl border p-4 shadow-2xl ${
      dark ? "border-blue-300/20 !bg-[#101d3a] text-slate-100" : "border-blue-200 !bg-[#eff6ff] text-slate-900"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold">{en ? "Notifications" : "Notifikasi"}</h3>
          {paymentOrderId && !dismissedPaymentOrderIds.includes(paymentOrderId) && (
            <button
              type="button"
              onClick={() => {
                const next = [...new Set([...dismissedPaymentOrderIds, paymentOrderId])];
                localStorage.setItem(
                  "pinmaker-dismissed-payment-notifications",
                  JSON.stringify(next)
                );
                setDismissedPaymentOrderIds(next);
              }}
              className="mt-1 text-xs font-semibold text-blue-500 hover:underline"
            >
              {en ? "Clear all" : "Bersihkan Semua"}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOrderNotificationOpen(false)}
          aria-label={en ? "Close notifications" : "Tutup notifikasi"}
          className={`rounded-lg p-1 text-slate-400 transition-colors ${dark ? "hover:bg-[#1b3d70] hover:text-white" : "hover:bg-[#dbeafe] hover:text-slate-900"}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {(!paymentOrderId || dismissedPaymentOrderIds.includes(paymentOrderId)) ? (
        <p className="mt-3 text-sm">
          {en ? "No notifications yet." : "Belum ada notifikasi."}
        </p>
      ) : (
        <>
          <p className="mt-3 break-all text-xs">
            Order ID: {paymentOrderId}
          </p>
          <p className={`mt-3 rounded-xl p-3 text-sm font-semibold ${
            ["paid", "success"].includes(paymentNotice?.status)
              ? (dark ? "bg-green-900/40 text-green-200" : "bg-green-100 text-green-800")
              : ["pending", "checking"].includes(paymentNotice?.status)
                ? (dark ? "bg-blue-900/40 text-blue-200" : "bg-blue-100 text-blue-800")
                : ["failed", "cancelled", "expired"].includes(paymentNotice?.status)
                  ? (dark ? "bg-red-900/40 text-red-200" : "bg-red-100 text-red-800")
                  : (dark ? "bg-[#1b3d70] text-blue-100" : "bg-[#dbeafe] text-blue-900")
          }`}>
            {paymentNotice?.order_id === paymentOrderId
              ? ({
                  paid: en ? "Payment successful" : "Pembayaran berhasil",
                  pending: en ? "Waiting for payment" : "Menunggu pembayaran",
                  checking: en ? "Checking status..." : "Memeriksa status...",
                  failed: en ? "Payment failed" : "Pembayaran gagal",
                  cancelled: en ? "Payment cancelled" : "Pembayaran dibatalkan",
                  expired: en ? "Payment expired" : "Pembayaran kedaluwarsa",
                }[paymentNotice.status] || paymentNotice.status)
              : (en ? "Checking status..." : "Memeriksa status...")}
          </p>
          {(paymentResumeToken || (paymentNotice?.order_type === "renewal" && renewalToken)) &&
            paymentNotice?.order_id === paymentOrderId &&
            (["pending", "checking"].includes(paymentNotice.status)) && (
              <Button type="button" className="mt-3 w-full" disabled={resumingPayment} onClick={resumePayment}>
                {resumingPayment ? (en ? "Opening payment..." : "Membuka pembayaran...") : (en ? "Resume Payment" : "Lanjutkan Pembayaran")}
              </Button>
            )}
        </>
      )}
    </div>
  )}
</div>
        

      </div>

      {!expiredLockPage && supportOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        >
          <div className={`w-full max-w-sm rounded-2xl border p-5 shadow-2xl ${
            dark ? "border-blue-300/20 !bg-[#101d3a] text-slate-100" : "border-blue-200 !bg-[#eff6ff] text-slate-900"
          }`}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">Contact / Support</h2>
                <p className={`mt-1 text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>
                  Hubungi developer jika membutuhkan bantuan.
                </p>
              </div>
              <button type="button" onClick={() => setSupportOpen(false)} aria-label="Tutup">
                <X className="h-5 w-5" />
              </button>
            </div>

            <a
              href="https://wa.me/6281545901447"
              target="_blank"
              rel="noopener noreferrer"
              className={`mb-3 flex items-center gap-3 rounded-xl border p-3 ${
                dark ? "border-blue-300/20 bg-[#1b3d70] hover:bg-[#244b80]" : "border-blue-200 bg-[#dbeafe] hover:bg-[#bfdbfe]"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">WhatsApp Support</span>
                <span className="block break-all text-sm text-green-600">0815-4590-1447</span>
                <span className="block text-xs text-slate-500">Klik untuk membuka WhatsApp</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0" />
            </a>

            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=fr.digitalprintingstudio@gmail.com" target="_blank" rel="noopener noreferrer"
              className={`flex items-center gap-3 rounded-xl border p-3 ${
                dark ? "border-blue-300/20 bg-[#1b3d70] hover:bg-[#244b80]" : "border-blue-200 bg-[#dbeafe] hover:bg-[#bfdbfe]"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Email Developer</span>
                <span className="block break-all text-sm text-blue-600">fr.digitalprintingstudio@gmail.com</span>
                <span className="block text-xs text-slate-500">Klik untuk mengirim email</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0" />
            </a>
          </div>
        </div>
      )}

      {!expiredLockPage && logoutConfirmOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setLogoutConfirmOpen(false);
          }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
            aria-describedby="logout-confirm-description"
            className={`w-full max-w-sm rounded-2xl border p-6 shadow-2xl ${
              dark
                ? "!border-blue-300/20 !bg-[#101d3a] !text-slate-100"
                : "!border-blue-200 !bg-[#eff6ff] !text-slate-900"
            }`}
          >
            <h2 id="logout-confirm-title" className="text-lg font-bold">
              Konfirmasi Keluar
            </h2>
            <p
              id="logout-confirm-description"
              className={`mt-2 text-sm ${
                dark ? "text-slate-300" : "text-slate-600"
              }`}
            >
              Yakin ingin keluar dari API Key? Sesi API Key Anda akan diakhiri.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                className={`rounded-xl border ${
                  dark
                    ? "border-blue-300/30 bg-[#1b3d70] text-slate-100 hover:bg-[#244b80] hover:text-white"
                    : "border-blue-200 bg-[#dbeafe] text-[#164078] hover:bg-[#bfdbfe] hover:text-[#12345a]"
                }`}
                onClick={() => setLogoutConfirmOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="button"
                className="rounded-xl !bg-red-600 text-white hover:!bg-red-700"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  logoutToAccount();
                }}
              >
                Ya, Keluar
              </Button>
            </div>
          </section>
        </div>
      )}

      <div className={`relative z-10 w-full ${expiredLockPage ? "max-w-md" : "max-w-xl"}`}>
        <div className={`pms-login-card ${expiredLockPage
          ? "rounded-3xl border-0 bg-transparent p-0 shadow-none"
          : `rounded-[2rem] border p-6 shadow-[0_20px_60px_-15px_rgba(30,64,175,0.18)] sm:p-12 ${
              dark
                ? "border-blue-400/30 bg-slate-900/75 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-2xl"
                : "border-white/90 bg-white/80 shadow-[0_24px_80px_rgba(59,130,246,0.20)] backdrop-blur-2xl"
            }`
        }`}>
          {!expiredLockPage && <div className="mb-8 flex flex-col items-center text-center">
            <h1 className="flex items-center justify-center gap-1 whitespace-nowrap text-4xl font-extrabold tracking-tight sm:text-5xl">
              <span className={dark ? "text-white" : "text-slate-900"}>PIN Maker</span>
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-purple-500 bg-clip-text text-transparent">Studio</span>
            </h1>
            <p className={`mt-3 text-sm sm:text-base ${dark ? "text-slate-300" : "text-slate-600"}`}>
              {t.subtitle}
            </p>
          </div>}

          {!expiredKeyForRenewal && !expiredLockPage ? (
            <form onSubmit={submit} className="space-y-5">
              <div className="relative">
                <KeyRound className={`absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 ${dark ? "text-slate-400" : "text-slate-500"}`} />
                <Input
                  id="api-key"
                  type="password"
                  data-testid="login-key-input"
                  aria-label={t.placeholder}
                  placeholder={t.placeholder}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  className={`h-16 rounded-2xl border pl-12 text-base transition-all duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${dark ? "border-blue-400/25 bg-slate-950/70 text-slate-100 placeholder:text-slate-500 shadow-inner shadow-black/20" : "border-blue-200/80 bg-white/90 text-slate-900 placeholder:text-slate-400 shadow-inner shadow-blue-100/60 hover:border-cyan-300"}`}
                />
              </div>
              <Button type="submit" className="h-16 w-full rounded-2xl border border-white/20 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 text-base font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110 dark:from-cyan-500 dark:via-blue-600 dark:to-violet-600" data-testid="login-submit-button" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                {loading ? t.loading : t.login}
              </Button>
            </form>
          ) : (
            <section className={`rounded-3xl border p-6 shadow-[0_24px_80px_rgba(0,0,0,0.28)] sm:p-8 ${dark ? "border-blue-300/25 bg-[#101d36]" : "border-blue-200 bg-white shadow-blue-950/10"}`}>
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-500"><ShieldCheck className="h-6 w-6" /></div>
              <h2 className="text-center text-xl font-bold">{renewalFromReminder ? (en ? "Renew your API Key" : "Perpanjang API Key") : (en ? "API Key is no longer active" : "API Key sudah tidak aktif")}</h2>
              <p className={`mt-2 text-center text-sm leading-6 ${dark ? "text-slate-300" : "text-slate-600"}`}>
                {renewalFromReminder
                  ? (en ? "Verify the registered email to continue. Your existing key and saved projects will remain the same." : "Verifikasi email yang terdaftar untuk melanjutkan. API Key lama dan proyek yang tersimpan akan tetap sama.")
                  : (en ? "Your editing access is locked because the active period has ended. Renew your API Key to restore access. Your saved projects remain stored." : "Akses pengeditan terkunci karena masa aktif API Key telah berakhir. Perpanjang API Key untuk mendapatkan akses kembali. Proyek yang tersimpan tetap aman.")}
              </p>

              {!expiredKeyForRenewal && renewalStep === "locked" && (
                <div className="mt-5 space-y-3">
                  <Input
                    id="renewal-api-key"
                    type="password"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    placeholder={en ? "Enter your existing API Key" : "Masukkan API Key lama"}
                    autoComplete="current-password"
                    className="h-12"
                  />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Button type="button" disabled={renewalBusy || !key.trim()} onClick={() => beginRenewalVerification(key.trim())} className="h-12 bg-blue-600 font-bold text-white hover:bg-blue-700">
                      {renewalBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {en ? "Renew API Key" : "Perpanjangan API Key"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { localStorage.removeItem("pp_expired"); localStorage.removeItem("pp_expired_notice_shown"); setExpiredLockPage(false); setExpiredKeyForRenewal(""); setRenewalStep("locked"); setRenewalOtp(""); setRenewalToken(""); setRenewalEmailRequired(false); setRenewalEmailInput(""); setKey(""); }} className="h-12">
                      {en ? "Exit" : "Keluar"}
                    </Button>
                  </div>
                </div>
              )}

              {expiredKeyForRenewal && renewalStep === "locked" && (
                <div className="mt-5 space-y-3">
                  {renewalEmailRequired && (
                    <div className="space-y-1.5">
                      <label htmlFor="renewal-owner-email" className={`block text-sm font-medium ${dark ? "text-slate-200" : "text-slate-700"}`}>
                        {en ? "Owner email address" : "Email pemilik API Key"}
                      </label>
                      <Input
                        id="renewal-owner-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={renewalEmailInput}
                        onChange={(e) => setRenewalEmailInput(e.target.value)}
                        placeholder="nama@email.com"
                        maxLength={254}
                        className="h-12"
                      />
                      <p className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
                        {en
                          ? "This Admin-created API Key has no owner email yet. We will save this address only after OTP verification succeeds."
                          : "API Key ini belum memiliki email pemilik. Email baru akan disimpan setelah OTP berhasil diverifikasi."}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button type="button" disabled={renewalBusy || (renewalEmailRequired && !renewalEmailInput.trim())} onClick={() => beginRenewalVerification(expiredKeyForRenewal, renewalEmailInput)} className="h-12 bg-blue-600 font-bold text-white hover:bg-blue-700">
                    {renewalBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {en ? "Renew API Key" : "Perpanjangan API Key"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { localStorage.removeItem("pp_expired"); localStorage.removeItem("pp_expired_notice_shown"); setExpiredKeyForRenewal(""); setExpiredLockPage(false); setRenewalFromReminder(false); setRenewalStep("locked"); setRenewalOtp(""); setRenewalToken(""); setRenewalEmailRequired(false); setRenewalEmailInput(""); setKey(""); }} className="h-12">
                    {en ? "Exit" : "Keluar"}
                  </Button>
                  </div>
                </div>
              )}

              {renewalStep === "otp" && (
                <div className="mt-5 space-y-3">
                  <p className={`text-center text-sm ${dark ? "text-blue-200" : "text-blue-800"}`}>{en ? `Enter the 6-digit code sent to ${renewalEmailHint}.` : `Masukkan kode 6 angka yang dikirim ke ${renewalEmailHint}.`}</p>
                  <Input value={renewalOtp} onChange={(e) => setRenewalOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" className="h-14 text-center text-2xl font-bold tracking-[0.4em]" />
                  <Button type="button" disabled={renewalBusy || renewalOtp.length !== 6} onClick={verifyRenewalOtp} className="h-12 w-full bg-blue-600 font-bold text-white hover:bg-blue-700">
                    {renewalBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{en ? "Verify code" : "Verifikasi Kode"}
                  </Button>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                    <button type="button" onClick={() => setRenewalStep("locked")} className="text-blue-500 underline">{en ? "Back" : "Kembali"}</button>
                    <button type="button" disabled={renewalBusy} onClick={() => beginRenewalVerification(expiredKeyForRenewal, renewalEmailInput)} className="text-blue-500 underline disabled:opacity-50">{en ? "Resend code" : "Kirim ulang kode"}</button>
                    <button type="button" onClick={() => { localStorage.removeItem("pp_expired"); localStorage.removeItem("pp_expired_notice_shown"); setExpiredKeyForRenewal(""); setExpiredLockPage(false); setRenewalFromReminder(false); setRenewalStep("locked"); setRenewalOtp(""); setRenewalToken(""); setKey(""); }} className="text-red-500 underline">{en ? "Exit" : "Keluar"}</button>
                  </div>
                </div>
              )}

              {renewalStep === "plans" && (
                <div className="mt-5 space-y-3">
                  <p className={`text-center text-sm font-semibold ${dark ? "text-slate-200" : "text-slate-700"}`}>{en ? "Choose your renewal package" : "Pilih paket perpanjangan"}</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button type="button" disabled={renewalBusy} onClick={() => startRenewalCheckout("starter")} className="rounded-xl border border-blue-400/30 bg-blue-500/10 p-4 text-left transition hover:border-blue-500 hover:bg-blue-500/15 disabled:opacity-50">
                      <span className="block text-base font-bold">Starter</span>
                      {getPlanPricing("starter", 50000, 30).discountActive && <span className="mt-1 block text-xs text-slate-500 line-through">{formatRupiah(getPlanPricing("starter", 50000, 30).originalPrice)}</span>}
                      <span className="mt-1 block text-xl font-extrabold text-blue-500">{formatRupiah(getPlanPricing("starter", 50000, 30).price)}</span>
                      <span className="mt-1 block text-xs">{en ? `${getPlanPricing("starter", 50000, 30).days} days` : `${getPlanPricing("starter", 50000, 30).days} hari`}</span>
                    </button>
                    <button type="button" disabled={renewalBusy} onClick={() => startRenewalCheckout("pro")} className="rounded-xl border border-violet-400/30 bg-violet-500/10 p-4 text-left transition hover:border-violet-500 hover:bg-violet-500/15 disabled:opacity-50">
                      <span className="block text-base font-bold">Pro</span>
                      {getPlanPricing("pro", 120000, 90).discountActive && <span className="mt-1 block text-xs text-slate-500 line-through">{formatRupiah(getPlanPricing("pro", 120000, 90).originalPrice)}</span>}
                      <span className="mt-1 block text-xl font-extrabold text-violet-500">{formatRupiah(getPlanPricing("pro", 120000, 90).price)}</span>
                      <span className="mt-1 block text-xs">{en ? `${getPlanPricing("pro", 120000, 90).days} days` : `${getPlanPricing("pro", 120000, 90).days} hari`}</span>
                    </button>
                  </div>
                  {renewalBusy && <p className="text-center text-sm text-blue-500">{en ? "Preparing payment..." : "Menyiapkan pembayaran..."}</p>}
                  {pendingPlanChange && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/75 p-4" role="dialog" aria-modal="true" aria-labelledby="pending-package-change-title">
                      <div className={`w-full max-w-md rounded-2xl border p-5 shadow-2xl ${dark ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"}`}>
                        <h3 id="pending-package-change-title" className="text-lg font-extrabold">{en ? "Unpaid renewal transaction" : "Transaksi perpanjangan belum dibayar"}</h3>
                        <p className={`mt-2 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
                          {en
                              ? ("You still have " + pendingPlanChange.pending_plan_name + " (" + formatRupiah(Number(pendingPlanChange.pending_amount || 0)) + ") pending. Continue it or replace it with " + pendingPlanChange.requested_plan_name + " (" + formatRupiah(Number(pendingPlanChange.requested_amount || 0)) + ")?")
                              : ("Kamu masih memiliki transaksi " + pendingPlanChange.pending_plan_name + " (" + formatRupiah(Number(pendingPlanChange.pending_amount || 0)) + ") yang belum dibayar. Lanjutkan transaksi tersebut atau ganti ke " + pendingPlanChange.requested_plan_name + " (" + formatRupiah(Number(pendingPlanChange.requested_amount || 0)) + ")?")}
                        </p>
                        <div className="mt-5 grid grid-cols-1 gap-2">
                          <Button type="button" disabled={renewalBusy} onClick={() => {
                            const pending = pendingPlanChange;
                            setPendingPlanChange(null);
                            startRenewalCheckout(pending.pending_plan);
                          }} className="h-11 w-full bg-blue-600 font-bold text-white hover:bg-blue-700">
                            {en ? `Continue ${pendingPlanChange.pending_plan_name}` : `Lanjutkan ${pendingPlanChange.pending_plan_name}`}
                          </Button>
                          <Button type="button" disabled={renewalBusy} variant="outline" onClick={() => {
                            const pending = pendingPlanChange;
                            setPendingPlanChange(null);
                            startRenewalCheckout(pending.requested_plan, pending.pending_order_id);
                          }} className="h-11 w-full font-bold">
                            {en ? `Replace with ${pendingPlanChange.requested_plan_name}` : `Ganti ke ${pendingPlanChange.requested_plan_name}`}
                          </Button>
                        </div>
                        <button type="button" onClick={() => setPendingPlanChange(null)} className={`mt-3 w-full text-sm underline ${dark ? "text-slate-400" : "text-slate-500"}`}>
                          {en ? "Cancel" : "Batal"}
                        </button>
                      </div>
                    </div>
                  )}
                  <button type="button" onClick={() => { localStorage.removeItem("pp_expired"); localStorage.removeItem("pp_expired_notice_shown"); setExpiredKeyForRenewal(""); setExpiredLockPage(false); setRenewalFromReminder(false); setRenewalStep("locked"); setKey(""); setRenewalToken(""); }} className="w-full text-sm text-blue-500 underline">{en ? "Exit" : "Keluar"}</button>
                </div>
              )}
            </section>
          )}

          {!expiredKeyForRenewal && !expiredLockPage && <button
            type="button"
            onClick={() => setApiContactOpen(true)}
            className="mt-5 w-full text-center text-sm font-semibold text-blue-600 transition-colors hover:text-cyan-600 dark:text-cyan-400 dark:hover:text-violet-400"
            data-testid="login-api-contact-button"
          >
            {en ? (
              <>Don't have an API Key? <span className={`font-extrabold no-underline ${dark ? "text-white" : "text-black"}`}>Click here</span></>
            ) : (
              <>Belum punya API Key? <span className={`font-extrabold no-underline ${dark ? "text-white" : "text-black"}`}>Klik di sini</span></>
            )}

          </button>}
        </div>
      </div>

      {!expiredLockPage && <p className={`absolute bottom-5 left-0 right-0 z-10 px-4 text-center text-xs ${dark ? "text-slate-400" : "text-slate-600"}`}>© {new Date().getFullYear()} PIN Maker Studio. {en ? "All rights reserved." : "Semua hak dilindungi undang-undang."}</p>}
      {!expiredLockPage && apiContactOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-md"
          onClick={() => setApiContactOpen(false)}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="api-contact-title"
            className={`relative my-auto flex min-h-[320px] w-full max-w-2xl flex-col justify-center rounded-3xl border p-8 shadow-2xl sm:p-10 ${
              dark
                ? "border-blue-400/30 bg-slate-900 text-slate-100"
                : "border-slate-200 bg-white text-slate-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label={en ? "Close dialog" : "Tutup dialog"}
              onClick={() => setApiContactOpen(false)}
              className="absolute right-4 top-4 rounded-lg p-2 hover:bg-slate-500/10"
            >
              <X className="h-5 w-5" />
            </button>

            {paymentNotice?.order_id ? (
              <div className="py-10 text-center">
                {paymentNotice.status === "paid" ? (
                  <>
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl text-green-600">✓</div>
                    <h2 className="mt-5 text-2xl font-bold">
                      {en ? "Payment successful!" : "Pembayaran berhasil!"}
                    </h2>
                    <p className={`mt-3 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
                      {paymentNotice.order_type === "renewal"
                        ? (en
                            ? "Your existing API Key has been renewed. Use the same API Key to continue."
                            : "API Key lama kamu berhasil diperpanjang. Gunakan API Key yang sama untuk melanjutkan.")
                        : (en
                            ? "Your payment has been verified by the server. Check your email for API Key information."
                            : "Pembayaran sudah dikonfirmasi oleh server. Silakan periksa email untuk informasi API Key.")}
                    </p>
                    <button
                      type="button"
                      onClick={async () => {
                        const isRenewal = paymentNotice.order_type === "renewal";
                        setApiContactOpen(false);
                        setPaymentNotice(null);
                        const closedOrderId = paymentOrderId;
                        localStorage.removeItem("pinmaker-last-order-id");
                        if (closedOrderId) localStorage.removeItem("pinmaker-resume-token-" + closedOrderId);
                        setPaymentResumeToken("");
                        setPaymentOrderId("");
                        if (isRenewal && key.trim()) {
                          try {
                            await loginWithKey(key.trim());
                            localStorage.removeItem("pp_expired");
                            localStorage.removeItem("pp_expired_notice_shown");
                            toast.success(en ? "API Key renewed. Welcome back!" : "API Key berhasil diperpanjang. Selamat datang kembali!");
                            navigate("/");
                          } catch (error) {
                            toast.error(error?.response?.data?.detail || (en ? "Sign in again with your API Key." : "Silakan masuk kembali menggunakan API Key kamu."));
                          }
                        }
                      }}
                      className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 font-bold text-white"
                    >
                      {paymentNotice.order_type === "renewal"
                        ? (en ? "Continue to PIN Maker Studio" : "Lanjutkan ke PIN Maker Studio")
                        : (en ? "Close" : "Tutup")}
                    </button>
                  </>
                ) : ["cancelled", "expired", "failed"].includes(paymentNotice.status) ? (
                  <>
                    <div className="mx-auto flex h-16 w-16 items-center justify-center">
  <svg viewBox="0 0 64 64" className="h-16 w-16" role="img" aria-label="Warning">
    <path d="M27.1 9.5a5.7 5.7 0 0 1 9.8 0l22.3 38.6a5.7 5.7 0 0 1-4.9 8.6H9.7a5.7 5.7 0 0 1-4.9-8.6L27.1 9.5Z"
      fill="#FEE2E2" stroke="#F87171" strokeWidth="2.8" strokeLinejoin="round" />
    <path d="M32 22v15" stroke="#DC2626" strokeWidth="5" strokeLinecap="round" />
    <circle cx="32" cy="45" r="2.8" fill="#DC2626" />
  </svg>
</div>
                    <h2 className="mt-5 text-2xl font-bold">
                      {paymentNotice.status === "cancelled"
                        ? (en ? "Payment cancelled" : "Pembayaran dibatalkan")
                        : paymentNotice.status === "expired"
                          ? (en ? "Payment expired" : "Pembayaran kedaluwarsa")
                          : (en ? "Payment failed" : "Pembayaran gagal")}
                    </h2>
                    <p className={`mt-3 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
                      {paymentNotice.status === "cancelled"
                        ? (en ? "The transaction was cancelled. You can choose a package to try again." : "Transaksi dibatalkan. Kamu bisa memilih paket untuk mencoba lagi.")
                        : paymentNotice.status === "expired"
                          ? (en ? "The payment period has expired. Please choose a package again." : "Batas waktu pembayaran sudah habis. Silakan pilih paket kembali.")
                          : (en ? "The payment could not be completed. Please try again." : "Pembayaran tidak berhasil. Silakan coba lagi.")}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const wasRenewal = paymentNotice.order_type === "renewal";
                        setPaymentNotice(null);
                        setPaymentOrderId("");
                        setRetryOrderId("");
                        setCheckoutProcessingOpen(false);
                        if (wasRenewal) {
                          setApiContactOpen(false);
                          setRenewalStep("locked");
                          setRenewalToken("");
                        } else {
                          setSelectedPlan(null);
                          setBuyerName("");
                          setBuyerEmail("");
                          setBuyerPhone("");
                        }
                      }}
                      className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 font-bold text-white"
                    >
                      {paymentNotice.order_type === "renewal"
                        ? (en ? "Back to renewal" : "Kembali ke Perpanjangan")
                        : (en ? "Choose package again" : "Pilih Paket Lagi")}
                    </button>
                  </>
                ) : (
                  <>
                    {paymentNotice.status === "pending" ? (
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : (
                      <div className="mx-auto h-14 w-14 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                    )}
                    <h2 className="mt-5 text-2xl font-bold">
                      {paymentNotice.status === "pending"
                        ? (en ? "Waiting for payment" : "Menunggu pembayaran")
                        : (en ? "Payment is being processed" : "Pembayaran sedang diproses")}
                    </h2>
                    <p className={`mt-3 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
                      {paymentNotice.status === "pending"
                        ? (en
                            ? "Complete the payment in Midtrans. This page will update when the server receives the result."
                            : "Selesaikan pembayaran di Midtrans. Halaman ini akan diperbarui setelah server menerima hasilnya.")
                        : (en
                            ? "The server is verifying your payment. Please do not pay again."
                            : "Server sedang memverifikasi pembayaran kamu. Jangan melakukan pembayaran ulang.")}
                    </p>
                    <p className="mt-4 text-xs text-slate-500">
                      {en ? "Order ID: " : "ID Pesanan: "}{paymentNotice.order_id}
                    </p>
                    {paymentNotice.status === "pending" && (
                      <div className="mt-5 grid grid-cols-1 gap-2">
                        <button
                          type="button"
                          disabled={resumingPayment || cancellingPayment || (!paymentResumeToken && !(paymentNotice.order_type === "renewal" && renewalToken))}
                          onClick={resumePayment}
                          className="h-12 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 font-bold text-white shadow-lg shadow-blue-950/20 disabled:opacity-50"
                        >
                          {resumingPayment
                            ? (en ? "Opening payment..." : "Membuka pembayaran...")
                            : (en ? "Continue Payment" : "Lanjutkan Pembayaran")}
                        </button>
                        
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
            <h2 id="api-contact-title" className="pr-8 text-2xl font-bold">
              {en ? "Choose an API Key package" : "Pilih Paket API Key"}
            </h2>
            <p className={`mt-2 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
              {en
                ? "Choose a package to continue."
                : "Pilih paket yang sesuai dengan kebutuhan kamu."}
            </p>

            {!selectedPlan && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                {
                  id: "demo",
                  name: "Demo",
                  price: en ? "Free" : "Gratis",
                  duration: en ? "3 days" : "3 hari",
                  description: en ? "Try the application" : "Coba aplikasi",
                },
                {
                  id: "starter",
                  name: "Starter",
                  price: formatRupiah(getPlanPricing("starter", 50000, 30).price),
                  originalPrice: getPlanPricing("starter", 50000, 30).originalPrice,
                  discountActive: getPlanPricing("starter", 50000, 30).discountActive,
                  duration: en ? `${getPlanPricing("starter", 50000, 30).days} days` : `${getPlanPricing("starter", 50000, 30).days} hari`,
                  description: en ? "For regular use" : "Untuk penggunaan rutin",
                },
                {
                  id: "pro",
                  name: "Pro",
                  price: formatRupiah(getPlanPricing("pro", 120000, 90).price),
                  originalPrice: getPlanPricing("pro", 120000, 90).originalPrice,
                  discountActive: getPlanPricing("pro", 120000, 90).discountActive,
                  duration: en ? `${getPlanPricing("pro", 120000, 90).days} days` : `${getPlanPricing("pro", 120000, 90).days} hari`,
                  description: en ? "For extended use" : "Masa aktif lebih panjang",
                },
              ].map((plan) => (
                <div
                  key={plan.id}
                  className={`flex flex-col rounded-2xl border p-4 ${
                    dark
                      ? "border-slate-700 bg-slate-950/60"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <h3 className="text-lg font-bold">{plan.name}</h3>
                  {plan.discountActive && (
                    <p className="mt-3 text-xs text-slate-500 line-through">{formatRupiah(plan.originalPrice)}</p>
                  )}
                  <p className={`${plan.discountActive ? "mt-1" : "mt-3"} text-xl font-extrabold text-blue-600 dark:text-cyan-400`}>
                    {plan.price}
                  </p>
                  <p className="mt-1 text-sm font-semibold">{plan.duration}</p>
                  <p className={`mt-2 flex-1 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
                    {plan.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className="mt-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-3 text-sm font-bold text-white transition hover:brightness-110"
                  >
                    {en ? "Choose package" : "Pilih Paket"}
                  </button>
                </div>
              ))}
            </div>
            )}

            {selectedPlan && (
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-bold">
                  {en ? "Your details" : "Data Pembeli"} — {
                    selectedPlan === "demo" ? "Demo" :
                    selectedPlan === "starter" ? "Starter" : "Pro"
                  }
                </h3>
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {en ? "Full name" : "Nama Pembeli"}
                  </label>
                  <input
                    type="text"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    placeholder={en ? "Your full name" : "Masukkan nama lengkap"}
                    autoComplete="name"
                    maxLength={80}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Email</label>
                  <input
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    placeholder="nama@gmail.com"
                    autoComplete="email"
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {en ? "Phone / WhatsApp number" : "Nomor Telepon / WhatsApp"}
                  </label>
                  <input
                    type="tel"
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                    placeholder="08xxxxxxxxxx"
                    autoComplete="tel"
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPlan(null);
                      setBuyerName("");
                      setBuyerEmail("");
                      setBuyerPhone("");
                    }}
                    className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold"
                  >
                    {en ? "Back to packages" : "Kembali ke Paket"}
                  </button>
                  <button
                    type="button"
                    disabled={
                      storeSubmitting ||
                      buyerName.trim().length < 2 ||
                      !buyerEmail.includes("@") ||
                      buyerPhone.trim().length < 8
                    }
                    onClick={() => setConfirmOpen(true)}
                    
                    className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
                  >
                    {storeSubmitting
                      ? (en ? "Processing..." : "Memproses...")
                      : selectedPlan === "demo"
                        ? (en ? "Get Demo API Key" : "Kirim API Key Demo")
                        : (en ? "Continue" : "Lanjutkan")}
                  </button>
                </div>
              </div>
            )}

              </>
            )}
          </section>
        </div>
      )}

      {checkoutProcessingOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <section
            role="dialog"
            aria-modal="true"
            aria-live="polite"
            className={`relative my-auto min-h-[320px] w-full max-w-2xl rounded-3xl border p-8 sm:p-10 text-center shadow-2xl flex flex-col items-center justify-center ${
              dark
                ? "border-slate-700 bg-slate-900 text-slate-100"
                : "border-slate-200 bg-white text-slate-900"
            }`}
          >
            {midtransOpen ? (
              <>
                <div className="mx-auto h-14 w-14 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                <h2 className="mt-5 text-xl font-bold">
                  {en ? "Payment in Progress" : "Pembayaran Sedang Berlangsung"}
                </h2>
                <p className={`mt-3 text-sm ${
                  dark ? "text-slate-300" : "text-slate-600"
                }`}>
                  {en
                    ? "Please complete or close the Midtrans payment window."
                    : "Silakan selesaikan pembayaran atau tutup jendela Midtrans."}
                </p>
              </>
            ) : paymentNotice?.status === "pending" ? (
              <>
                <button
                  type="button"
                  aria-label={en ? "Close payment window" : "Tutup jendela pembayaran"}
                  title={en ? "Close" : "Tutup"}
                  onClick={() => setCheckoutProcessingOpen(false)}
                  className={`absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border text-2xl leading-none transition ${
                    dark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                      : "border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  ×
                </button>

                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-14 w-14 text-blue-500"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <h2 className="mt-5 text-xl font-bold">
                  {en ? "Waiting for Payment" : "Menunggu Pembayaran"}
                </h2>

                <p className={`mt-3 max-w-lg text-sm ${
                  dark ? "text-slate-300" : "text-slate-600"
                }`}>
                  {en
                    ? "Your order is waiting for payment. Continue before the checkout deadline."
                    : "Pesanan kamu masih menunggu pembayaran. Silakan lanjutkan sebelum batas waktu checkout berakhir."}
                </p>

                <div className="mt-7 flex w-full max-w-xs justify-center">
                  <button
                    type="button"
                    onClick={resumePayment}
                    disabled={
                      resumingPayment ||
                      cancellingPayment ||
                      (paymentNotice?.order_type === "renewal"
                        ? !renewalToken
                        : !paymentResumeToken)
                    }
                    className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {resumingPayment
                      ? (en ? "Opening Payment..." : "Membuka Pembayaran...")
                      : (en ? "Continue Payment" : "Lanjutkan Pembayaran")}
                  </button>
                </div>
              </>
            ) : paymentNotice?.status === "expired" ? (
              <>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-14 w-14 text-red-500"
                >
                  <path d="M12 3 22 20H2L12 3Z" strokeLinejoin="round" />
                  <path d="M12 9v5m0 3h.01" strokeLinecap="round" />
                </svg>

                <h2 className="mt-5 text-xl font-bold text-red-500">
                  {en ? "Payment Expired" : "Pembayaran Kedaluwarsa"}
                </h2>

                <p className={`mt-3 max-w-lg text-sm ${
                  dark ? "text-slate-300" : "text-slate-600"
                }`}>
                  {paymentNotice?.order_type === "renewal"
                    ? (en
                        ? "The payment period for this renewal order has expired. Please start the renewal process again."
                        : "Waktu pembayaran untuk pesanan perpanjangan ini telah habis. Silakan mulai proses perpanjangan kembali.")
                    : (en
                        ? "The payment period for this order has expired. Please choose a package again to continue purchasing your PIN Maker Studio API Key."
                        : "Waktu pembayaran untuk pesanan ini telah habis. Silakan buat pesanan baru untuk melanjutkan pembelian API Key PIN Maker Studio.")}
                </p>

                <button
                  type="button"
                  onClick={() => {
                    const expiredOrderId = paymentOrderId;
                    const wasRenewal = paymentNotice?.order_type === "renewal";

                    setCheckoutProcessingOpen(false);
                    setPaymentNotice(null);
                    setPaymentOrderId("");
                    setRetryOrderId("");
                    setPaymentResumeToken("");

                    localStorage.removeItem("pinmaker-last-order-id");
                    if (expiredOrderId) {
                      localStorage.removeItem("pinmaker-resume-token-" + expiredOrderId);
                    }

                    if (wasRenewal) {
                      setApiContactOpen(false);
                      setRenewalStep("locked");
                      setRenewalToken("");
                    } else {
                      setSelectedPlan(null);
                      setBuyerName("");
                      setBuyerEmail("");
                      setBuyerPhone("");
                      setApiContactOpen(true);
                    }
                  }}
                  className="mt-7 w-full max-w-xs rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-red-700"
                >
                  {paymentNotice?.order_type === "renewal"
                    ? (en ? "Back to Renewal" : "Kembali ke Perpanjangan")
                    : (en ? "Choose Package Again" : "Pilih Paket Lagi")}
                </button>
              </>
            ) : ["failed", "cancelled"].includes(paymentNotice?.status) ? (
              <>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-14 w-14 text-red-500"
                >
                  <path d="M12 3 22 20H2L12 3Z" strokeLinejoin="round" />
                  <path d="m9 10 6 6m0-6-6 6" strokeLinecap="round" />
                </svg>

                <h2 className="mt-5 text-xl font-bold">
                  {paymentNotice?.status === "cancelled"
                    ? (en ? "Order Cancelled" : "Pesanan Dibatalkan")
                    : (en ? "Payment Failed" : "Pembayaran Gagal")}
                </h2>

                <p className={`mt-3 text-sm ${
                  dark ? "text-slate-300" : "text-slate-600"
                }`}>
                  {en
                    ? "This payment could not be completed. You can close this message and try again."
                    : "Pembayaran ini tidak berhasil diselesaikan. Tutup pesan ini untuk mencoba kembali."}
                </p>

                <button
                  type="button"
                  onClick={() => setCheckoutProcessingOpen(false)}
                  className="mt-7 rounded-xl bg-slate-700 px-6 py-3 text-sm font-bold text-white hover:bg-slate-800"
                >
                  {en ? "Close" : "Tutup"}
                </button>
              </>
            ) : (
              <>
                <div className="mx-auto h-14 w-14 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                <h2 className="mt-5 text-xl font-bold">
                  {en ? "Your order is being processed" : "Pesanan sedang diproses"}
                </h2>
                <p className={`mt-3 text-sm ${
                  dark ? "text-slate-300" : "text-slate-600"
                }`}>
                  {en
                    ? "Please wait while we verify your payment status."
                    : "Mohon tunggu, kami sedang memeriksa status pembayaran kamu."}
                </p>
              </>
            )}
          </section>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget && !storeSubmitting) setConfirmOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-confirm-title"
            className={`my-auto flex min-h-[320px] w-full max-w-2xl flex-col justify-center rounded-3xl border p-8 shadow-2xl sm:p-10 ${
              dark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"
            }`}
          >
            <h2 id="checkout-confirm-title" className="text-xl font-bold">
              {en ? "Confirm your details" : "Konfirmasi data pembelian"}
            </h2>
            <p className={`mt-2 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
              {en ? "Check your details before continuing." : "Periksa data kamu sebelum melanjutkan."}
            </p>
            <dl className={`mt-5 space-y-3 rounded-xl p-4 text-sm ${dark ? "bg-slate-800" : "bg-slate-50"}`}>
              <div><dt className={dark ? "text-slate-400" : "text-slate-500"}>{en ? "Name" : "Nama"}</dt><dd className="mt-1 break-words font-semibold">{buyerName.trim()}</dd></div>
              <div><dt className={dark ? "text-slate-400" : "text-slate-500"}>Email</dt><dd className="mt-1 break-words font-semibold">{buyerEmail.trim()}</dd></div>
              <div><dt className={dark ? "text-slate-400" : "text-slate-500"}>WhatsApp</dt><dd className="mt-1 break-words font-semibold">{buyerPhone.trim()}</dd></div>
              <div><dt className={dark ? "text-slate-400" : "text-slate-500"}>{en ? "Package" : "Paket"}</dt><dd className="mt-1 font-semibold">{selectedPlan === "demo" ? "Demo" : selectedPlan === "starter" ? "Starter" : "Pro"}</dd></div>
            </dl>
            <div className="mt-6 flex gap-3">
              <button type="button" disabled={storeSubmitting} onClick={() => setConfirmOpen(false)}
                className={`flex-1 rounded-xl border px-3 py-3 text-sm font-semibold disabled:opacity-50 ${dark ? "border-slate-600 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-50"}`}>
                {en ? "Cancel" : "Batal"}
              </button>
              <button type="button" disabled={storeSubmitting} onClick={runStoreCheckout}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-3 text-sm font-bold text-white disabled:opacity-50">
                {storeSubmitting ? (en ? "Processing..." : "Memproses...") : (en ? "Confirm & Continue" : "Konfirmasi & Lanjutkan")}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
